/**
 * SearchableDropdown Component
 * A reusable dropdown with debounced search functionality
 * Extracted from RecipeIngredientsSection for reuse in recipe selection, etc.
 */

import { useState, useEffect, useRef, useCallback, useId } from 'react';
import { Input } from '@/components/ui/input';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

export interface SearchableDropdownProps<T> {
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Async function to search for items */
  onSearch: (query: string) => Promise<T[]>;
  /** Callback when an item is selected */
  onSelect: (item: T) => void;
  /** Render function for each dropdown item */
  renderItem: (item: T) => React.ReactNode;
  /** Get unique key for each item */
  getItemKey: (item: T) => string | number;
  /** Minimum characters before search triggers */
  minSearchLength?: number;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Message to show when no results found */
  emptyMessage?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Callback when the search input changes */
  onQueryChange?: (query: string) => void;
  /** Callback when no matches found - can be used for quick-create UI */
  onNoMatches?: (searchQuery: string) => void;
  /** Custom content to render when no matches found */
  noMatchesContent?: React.ReactNode | ((searchQuery: string) => React.ReactNode);
  /** Accessible name for the search input */
  ariaLabel?: string;
}

export function SearchableDropdown<T>({
  placeholder = 'Search...',
  onSearch,
  onSelect,
  renderItem,
  getItemKey,
  minSearchLength = MIN_SEARCH_LENGTH,
  debounceMs = SEARCH_DEBOUNCE_MS,
  disabled = false,
  emptyMessage = 'No matches found',
  className = '',
  onQueryChange,
  onNoMatches,
  noMatchesContent,
  ariaLabel,
}: SearchableDropdownProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Counter to discard stale search responses
  const searchRequestIdRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (containerRef.current?.contains(target)) {
        return;
      }
      // Check for Radix portals
      if (target.closest('[data-radix-popper-content-wrapper]') ||
          target.closest('[data-radix-select-viewport]') ||
          target.closest('[role="listbox"]')) {
        return;
      }
      setShowDropdown(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < minSearchLength) {
      setSearchResults([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    const requestId = ++searchRequestIdRef.current;

    const timeoutId = setTimeout(async () => {
      try {
        const results = await onSearch(searchQuery);
        if (requestId === searchRequestIdRef.current) {
          setSearchResults(results);
          setSearchError(null);
          // Notify about no matches if callback provided
          if (results.length === 0 && onNoMatches) {
            onNoMatches(searchQuery);
          }
        }
      } catch {
        if (requestId === searchRequestIdRef.current) {
          setSearchResults([]);
          setSearchError('Search failed');
        }
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setIsSearching(false);
        }
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, minSearchLength, debounceMs, onSearch, onNoMatches]);

  const handleSelect = useCallback((item: T) => {
    onSelect(item);
    setSearchQuery('');
    setShowDropdown(false);
    inputRef.current?.focus();
  }, [onSelect]);

  const noMatchesFound = searchQuery.length >= minSearchLength &&
    !isSearching &&
    !searchError &&
    searchResults.length === 0;
  const resolvedNoMatchesContent = typeof noMatchesContent === 'function'
    ? noMatchesContent(searchQuery)
    : noMatchesContent;

  // Generate unique ID for accessibility
  const listboxId = useId();
  const isExpanded = showDropdown && searchQuery.length >= minSearchLength;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? listboxId : undefined}
        aria-haspopup="listbox"
        placeholder={placeholder}
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setShowDropdown(true);
          onQueryChange?.(e.target.value);
        }}
        onFocus={() => setShowDropdown(true)}
        disabled={disabled}
        className="w-full"
      />

      {/* Dropdown */}
      {isExpanded && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {/* Searching indicator */}
          {isSearching && (
            <div className="px-4 py-2 text-sm text-gray-500" role="status" aria-live="polite">
              Searching...
            </div>
          )}

          {/* Search error */}
          {!isSearching && searchError && (
            <div className="px-4 py-2 text-sm text-red-600" role="alert">
              {searchError}
            </div>
          )}

          {/* Results */}
          {!isSearching && !searchError && searchResults.map((item) => (
            <button
              key={getItemKey(item)}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => handleSelect(item)}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex justify-between items-center"
            >
              {renderItem(item)}
            </button>
          ))}

          {/* No matches */}
          {noMatchesFound && (
            resolvedNoMatchesContent || (
              <div className="px-4 py-2 text-sm text-gray-500">
                {emptyMessage}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
